"use client";

import React, { useRef, useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { DatePickerSheet, pickerHost, useDateFormats } from "@/components/DatePickerSheet";
import { localTodayIso, nightsBetween } from "@/lib/date-range";

/**
 * Check-in / check-out picker for the booking site: one tap target that opens the shared calendar sheet (a bottom
 * sheet on phones), where the guest taps the arrival day and then the departure day. With `fixedNights` (a package)
 * only the arrival is picked and the departure follows.
 */
export function DateRangePicker({
  checkIn,
  checkOut,
  onChange,
  minDate,
  maxDate,
  fixedNights,
  className = "",
}: {
  checkIn: string;
  checkOut: string;
  onChange: (checkIn: string, checkOut: string) => void;
  minDate?: string;
  /** Latest date either end of the stay can be (the property's booking window). */
  maxDate?: string;
  fixedNights?: number;
  className?: string;
}) {
  const { t } = useI18n();
  const fmt = useDateFormats();
  const [host, setHost] = useState<Element | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const setOpen = (on: boolean) => setHost(on ? pickerHost(triggerRef.current) : null);
  const nights = nightsBetween(checkIn, checkOut);

  return (
    <>
      <button ref={triggerRef} type="button" className={`ps-input ps-date-trigger ${className}`} onClick={() => setOpen(true)} aria-haspopup="dialog">
        <span className="ps-date-part">
          <small>{t("publicsite.booking.checkIn")}</small>
          <strong>{checkIn ? fmt.short(checkIn) : t("publicsite.booking.dates.pick")}</strong>
        </span>
        <span className="ps-date-arrow" aria-hidden="true">{"→"}</span>
        <span className="ps-date-part">
          <small>{t("publicsite.booking.checkOut")}</small>
          <strong>{checkOut ? fmt.short(checkOut) : t("publicsite.booking.dates.pick")}</strong>
        </span>
        {nights > 0 && <span className="ps-date-nights">{t("publicsite.booking.nights", { count: nights })}</span>}
      </button>

      {host && (
        <DatePickerSheet
          mode="range"
          from={checkIn}
          to={checkOut}
          min={minDate || localTodayIso()}
          max={maxDate}
          fixedNights={fixedNights}
          title={t("publicsite.booking.dates.title")}
          startLabel={t("publicsite.booking.checkIn")}
          endLabel={t("publicsite.booking.checkOut")}
          host={host}
          onApply={(a, b) => {
            onChange(a, b);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/** − n + control for the number of guests (big tap targets instead of a dropdown). */
export function GuestStepper({ value, onChange, min = 1, max = 20, className = "" }: { value: number; onChange: (n: number) => void; min?: number; max?: number; className?: string }) {
  const { t } = useI18n();
  return (
    <div className={`ps-input ps-stepper ${className}`}>
      <button type="button" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} aria-label={t("publicsite.booking.dates.fewerGuests")}>{"−"}</button>
      <span aria-live="polite">{t("publicsite.booking.guestCount", { count: value })}</span>
      <button type="button" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} aria-label={t("publicsite.booking.dates.moreGuests")}>{"+"}</button>
    </div>
  );
}
